import { useState } from 'react';
import type {
  IUserSettingsCmsTerms,
  IUserSettingsPrpPredefinedField,
  IUserSettingsPrpUdf,
} from 'app/services/userSettingsApi';
import ProposalAppPaneBody from './ProposalAppPanes';
import { proposalCmsBody, type ProposalCmsDocument } from './proposalModel';

/**
 * Column 3 of the Proposal tab — `proposal.tmpl:175-411`, `#myTabContent`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ NO_Q1 — THE PROPOSAL PANES ARE **NOT** LAZY. ★★
 *
 * This is the single biggest structural difference from the Submittal tab and it
 * must not be "harmonised". Submittal renders 283 EMPTY `tab-pane`s and fills
 * one at a time from `loadPdf` on `mouseover` (★ Q1), so the right-hand column
 * is blank on first paint. Proposal has no `loadPdf`, no `mouseover` binding on
 * the library anchors, and no `filledIds` equivalent: `proposal.tmpl:179-215`
 * emits every user pane's body — the title `<input>` and the contenteditable —
 * inline, in the template. Discovery says it outright (§11 Q1: "**Proposal does
 * not do this** — its panes are fully rendered by the template").
 *
 * So: do NOT copy `filledIds`. There is nothing to fill.
 *
 * ★★ NO PDF, ANYWHERE. There is no `udf_type_value` on a `PRPUserDefinedField`,
 * no `<form id="pdf_upload_…">`, no `<input type="file">`, no `<iframe>`, no 30 MB
 * gate, no `viewPDF` fade and no Q15. A proposal user field is rich text and
 * nothing else. There is also no "Start from new page" checkbox, so ★ Q5 — the
 * submittal checkbox whose value never persists on its own — has no counterpart
 * on this tab at all.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * ★ BOOTSTRAP FADE SEMANTICS, same as submittal: `Tab.prototype.activate` adds
 * `.active` then `.in` (the pane carries `.fade`); deactivation removes only
 * `.active`, so `.in` is sticky. `panesWithIn` is that sticky half.
 *
 * ★ THE PANE ID AND THE PANE CLASS USE DIFFERENT KEYS, AND BOTH ARE READ.
 *   `id="proposal_user_defined_field_{{field_order}}"`  ← the anchor's `href`,
 *        and what `active_last_change` (`:295-300`) adds `.active in` to;
 *   `class="… proposal_user_defined_field_{{id}}"`      ← what `activateTab`
 *        (`:374`) and `removeUserDefinedField` (`:313`) select on.
 * They are NOT interchangeable, and on dev3 they legitimately collide across
 * different fields (one field's `id` equalling another's `field_order`).
 */

interface ProposalFieldDetailProps {
  /** The render snapshot's user rows. */
  userFields: IUserSettingsPrpUdf[];
  appFields: IUserSettingsPrpPredefinedField[];
  cmsDocuments: ProposalCmsDocument[];
  terms: IUserSettingsCmsTerms | undefined;
  /**
   * ★ `render()`'s generation counter. Legacy's `self.$el.html(...)` throws the
   * whole subtree away and rebuilds it from the collections, which is the ONLY
   * thing that resets a `contenteditable` the user has typed into and a title
   * `<input>` that owns its own value. Threading `gen` into each user pane's
   * React `key` reproduces that remount — and proposal renders FAR more often
   * than submittal does (every UDF save, every format-model `set`), so this is
   * load-bearing rather than cosmetic.
   */
  gen: number;
  /** Every pane carrying `.active`. ★ USUALLY ONE — Bootstrap's
   * `activate($target, $target.parent())` is exclusive within `#myTabContent` —
   * but `activateTab` (★ Q23) and `active_last_change` `addClass("active in")`
   * ADDITIVELY and the `$("#proposal #myTabContent .active").removeClass(...)`
   * that would undo it is dead under the `#proposal` trap. So two panes really
   * can be open at once here; verified against legacy in wave-4 verify. */
  activePaneIds: ReadonlySet<string>;
  /** Panes that have ever been activated — they keep Bootstrap's `.in`. */
  panesWithIn: ReadonlySet<string>;
  /** `blur .user_defined_field` → `updateUserDefinedField`, then
   * `blur .title_edit` → `saveUserDefinedFieldData`. */
  onTitleBlur: (field: IUserSettingsPrpUdf, value: string) => void;
  /** `blur #edit_textarea`, same pairing; the value is the raw `innerHTML`. */
  onContentBlur: (field: IUserSettingsPrpUdf, html: string) => void;
  /** ★ `input .user_defined_field` → `changeUserDefinedField` (`:239-244`).
   * THE GATE THAT WAVE 2 DELIBERATELY DID NOT PORT, because submittal never
   * reads its flag — `proposal.js:250` DOES. See `ProposalTab`. */
  onFieldInput: (attr: 'name' | 'content') => void;
  /** `keyup`/`change .textarea-field-title` → `checkField` (`:260-271`). Bound
   * on the CMS panes' readonly title inputs too, exactly as legacy does. */
  onCmsTitleCheck: (docValue: string, input: HTMLInputElement) => void;
  invalidCmsTitles: ReadonlySet<string>;
}

/**
 * `proposal.tmpl:203` puts `name="content"` on a `<div>` — not valid HTML for
 * that element, but `updateUserDefinedField` (`:248-255`) branches on
 * `elem.attr('name')` to decide between `elem.val()` and `elem.html()`, and the
 * attribute is in the rendered DOM. Spread through a cast.
 */
const CONTENT_NAME_ATTR = { name: 'content' } as unknown as React.HTMLAttributes<HTMLDivElement>;

/** One user pane's body. Split out only so `useState` for `checkField`'s
 * `.invalid-field` toggle can live per-pane, as it does per-input in legacy. */
const ProposalUserPane = ({
  field,
  onTitleBlur,
  onContentBlur,
  onFieldInput,
}: {
  field: IUserSettingsPrpUdf;
  onTitleBlur: (field: IUserSettingsPrpUdf, value: string) => void;
  onContentBlur: (field: IUserSettingsPrpUdf, html: string) => void;
  onFieldInput: (attr: 'name' | 'content') => void;
}) => {
  /** `checkField` (`:260-271`) — `.invalid-field` under 3 characters. ★ Q21: the
   * gate has no teeth. Its other half toggles `button[name="done_btn"]
   * .non-clickable`, and that button is inside an HTML comment
   * (`proposal.tmpl:208-213`) — which is also why `'click #done'` and
   * `'click #cancel'` are bound to nothing (§5.3 #7, #10). A 1-character name
   * still saves, because the blur-save never consults the flag. */
  const [invalid, setInvalid] = useState(false);

  return (
    <>
      <div className="col-xs-12 content-header">
        <div className="col-xs-6 content-title">
          <input
            type="text"
            name="name"
            className={`textarea-field-title title_edit user_defined_field title_user_defined_field_${
              field.id
            }${invalid ? ' invalid-field' : ''}`}
            minLength={3}
            maxLength={65}
            defaultValue={field.name ?? ''}
            data-id={field.id}
            onInput={() => onFieldInput('name')}
            onKeyUp={(e) => setInvalid(e.currentTarget.value.length < 3)}
            onChange={(e) => setInvalid(e.currentTarget.value.length < 3)}
            onBlur={(e) => onTitleBlur(field, e.currentTarget.value)}
          />
        </div>
        <div className="col-xs-6">
          <div className="col-xs-8" />
          <div className="col-xs-1" />
        </div>
      </div>
      <div className="col-xs-12 content-tooltip">
        <div className="col-xs-6 title-tooltip">
          <span>3-65 characters</span>
        </div>
      </div>
      <div className="row content-text ">
        <div className="col-xs-12">
          <div className="col-xs-12">
            {/* `{{{content}}}` — raw, unescaped HTML, read back out as
                `elem.html()` on blur. React must never own these children: the
                user's edits live in the DOM, exactly as they do under Backbone.
                The pane's `key` carries `gen`, so a `render()` remounts this and
                the body reverts to the collection's value — which is precisely
                what `$el.html()` does. ★ `id="edit_textarea"` is duplicated
                across every pane; jQuery's delegated `blur #edit_textarea`
                matches them all, so the duplication is load-bearing. */}
            <div
              className={`edit col-xs-12 content-editable textarea-field field_user_defined_field_${field.id} user_defined_field`}
              {...CONTENT_NAME_ATTR}
              id="edit_textarea"
              data-id={field.id}
              contentEditable
              suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: field.content ?? '' }}
              onInput={() => onFieldInput('content')}
              onBlur={(e) => onContentBlur(field, e.currentTarget.innerHTML)}
            />
          </div>
        </div>
      </div>
      {/* `proposal.tmpl:208-213` — the Done/Cancel block is an HTML comment in
          legacy. Not rendered. */}
    </>
  );
};

const ProposalFieldDetail = ({
  userFields,
  appFields,
  cmsDocuments,
  terms,
  gen,
  activePaneIds,
  panesWithIn,
  onTitleBlur,
  onContentBlur,
  onFieldInput,
  onCmsTitleCheck,
  invalidCmsTitles,
}: ProposalFieldDetailProps) => {
  const paneState = (id: string) =>
    `${activePaneIds.has(id) ? ' active' : ''}${panesWithIn.has(id) ? ' in' : ''}`;

  /**
   * ★★ THE USER PANE ID IS NOT UNIQUE, AND ONLY THE FIRST ONE MAY LIGHT UP.
   *
   * `id="proposal_user_defined_field_{{field_order}}"` is keyed by `field_order`,
   * which is unique only PER USER (`ksdm_prp_user_defined_field`'s
   * `(user_id, field_order, name)` constraint) — while a NETWORK user's library
   * is every network user's fields at once
   * (`PRPUserDefinedFieldViewSet.list` filters on
   * `user__access_type__value='network'`, not on `request.user`). So two rows
   * routinely share one `field_order` and therefore one DOM id.
   *
   * Legacy activates a pane only ever through an `#id` lookup — Bootstrap's
   * `Tab.show`'s `$(document).find($this.attr('href'))` and
   * `active_last_change`'s `$('#proposal_user_defined_field_' + field_order)`
   * (`proposal.js:299`) — and jQuery takes the `getElementById` fast path for a
   * bare `#id`, so BOTH resolve to the first match in document order and exactly
   * one pane is ever `display:block`.
   *
   * Keying off the id string alone lit up EVERY duplicate, and because
   * `.tab-pane` is a normal block the extras stacked BELOW the first one, adding
   * a full pane's height of blank page under the footer. Resolve the id the way
   * the DOM does: first match wins.
   */
  const firstPaneRow = new Map<string, number>();
  userFields.forEach((field) => {
    const id = `proposal_user_defined_field_${field.field_order}`;
    if (!firstPaneRow.has(id)) firstPaneRow.set(id, field.id);
  });

  return (
    <div className="col-xs-12 col-md-6 col-lg-8 left-column-scrollable app-field">
      <div className="row  full-height">
        <div className="col-xs-12 full-height">
          {/* ★ Q3's duplicate-id family again — `id="myTabContent"` also exists
              on the Submittal tab. Kept. */}
          <div className="col-xs-12  tab-content full-height pl-0" id="myTabContent">
            {/* ── USER — fully rendered, never lazy (see the NO_Q1 banner) ── */}
            {userFields.map((field) => {
              const id = `proposal_user_defined_field_${field.field_order}`;
              return (
                <div
                  key={`${gen}:${field.id}`}
                  className={`col-xs-12 pl-0 tab-pane fade proposal_user_defined_field_${field.id}${
                    firstPaneRow.get(id) === field.id ? paneState(id) : ''
                  }`}
                  id={id}
                  role="tabpanel"
                  aria-labelledby={`${id}-tab`}
                >
                  <ProposalUserPane
                    field={field}
                    onTitleBlur={onTitleBlur}
                    onContentBlur={onContentBlur}
                    onFieldInput={onFieldInput}
                  />
                </div>
              );
            })}

            {/* ── APPLICATION — three static mock-ups ───────────────────── */}
            {appFields.map((field) => (
              <div
                key={field.id}
                className={`col-xs-12 tab-pane fade${paneState(field.value)}`}
                /* ★ RAW `value` — no `submittal_` prefix on this tab. */
                id={field.value}
                role="tabpanel"
                aria-labelledby={`${field.value}-tab`}
              >
                <div className="col-xs-12">
                  <div className="col-xs-6 content-title non-editable">
                    <span>{field.name}</span>
                  </div>
                </div>
                <div className="col-xs-12 content-text scrollable">
                  <div className="col-xs-12">
                    <ProposalAppPaneBody value={field.value} />
                  </div>
                </div>
              </div>
            ))}

            {/* ── THE ORPHAN ───────────────────────────────────────────────
                `proposal.tmpl:372-388` hard-codes a "Sales Terms & Conditions"
                pane with `id="cms_terms_sales"`, while the `{{#each
                cms_documents}}` loop below emits `id="cms_terms_sales_terms"`
                and EVERY anchor points at the loop's version. So this pane is
                never activated — discovery §10 lists it as dead code inside a
                live file. It is rendered anyway: it is literal template output,
                it is invisible (a `.tab-pane` without `.active` is
                `display:none`), and `render()`'s
                `$('#proposal_format .sales_terms_textarea').append(...)` targets
                BOTH this div and the loop's, so both really do receive the sales
                terms HTML on the legacy page. */}
            <div
              className={`col-xs-12 tab-pane fade cms_terms_sales${paneState('cms_terms_sales')}`}
              id="cms_terms_sales"
              role="tabpanel"
              aria-labelledby="cms_terms_sales-tab"
            >
              <div className="col-xs-12 content-header">
                <div className="col-xs-6 non-editable">
                  <input
                    type="text"
                    name="name"
                    className="textarea-field-title"
                    defaultValue="Sales Terms & Conditions"
                    readOnly
                  />
                </div>
                <div className="col-xs-6">
                  <div className="col-xs-8" />
                  <div className="col-xs-1" />
                </div>
              </div>
              <div className="col-xs-12  content-text">
                <div
                  className="textarea-field col-xs-12  sales_terms_textarea"
                  dangerouslySetInnerHTML={{ __html: proposalCmsBody(terms, 'sales_terms') }}
                />
              </div>
            </div>

            {/* ── CMS — read-only, body injected as raw HTML ────────────── */}
            {cmsDocuments.map((doc) => (
              <div
                key={doc.value}
                className={`col-xs-12 tab-pane fade ${doc.key}${paneState(doc.key)}`}
                id={doc.key}
                role="tabpanel"
                aria-labelledby={`${doc.key}-tab`}
              >
                <div className="col-xs-12 content-header">
                  <div className="col-xs-6 non-editable">
                    {/* `readonly="true"` in the template — the whole reason the
                        CMS terms are unwritable from this page (§6 "Write paths
                        that DO NOT exist"). */}
                    <input
                      type="text"
                      name="name"
                      className={`textarea-field-title${
                        invalidCmsTitles.has(doc.value) ? ' invalid-field' : ''
                      }`}
                      defaultValue={doc.label}
                      readOnly
                      onKeyUp={(e) => onCmsTitleCheck(doc.value, e.currentTarget)}
                      onChange={(e) => onCmsTitleCheck(doc.value, e.currentTarget)}
                    />
                  </div>
                  <div className="col-xs-6">
                    <div className="col-xs-8" />
                    <div className="col-xs-1" />
                  </div>
                </div>
                <div className="col-xs-12  content-text">
                  {/* `render()` (`:169-173`) `.append()`s the terms HTML here.
                      Server-authored CMS boilerplate, rendered as markup on the
                      legacy page and as markup here. */}
                  <div
                    className={`textarea-field col-xs-12 ${doc.value}_textarea`}
                    dangerouslySetInnerHTML={{ __html: proposalCmsBody(terms, doc.value) }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalFieldDetail;
